import type { CSSProperties } from 'react';
import type { Cell, CellPlugin, DataTType } from '../types';

import classNames from 'classnames';

export const getCellInnerDivStyle = (
  cell: Cell | null,
  plugin: CellPlugin | null,
  data: DataTType
) =>
  cell && plugin?.cellStyle
    ? typeof plugin?.cellStyle === 'function'
      ? plugin?.cellStyle(data)
      : plugin?.cellStyle
    : undefined;

// Returns undefined for every plugin that doesn't opt in, so the outer cell's
// style prop stays exactly what it was before this existed.
export const getCellOuterDivStyle = (
  plugin: CellPlugin | null,
  data: DataTType
) =>
  plugin?.cellOuterStyle
    ? typeof plugin?.cellOuterStyle === 'function'
      ? plugin?.cellOuterStyle(data)
      : plugin?.cellOuterStyle
    : undefined;

// The pixel width a plugin pins its cell to, or undefined when the cell is sized
// by the 12 column grid like every other one.
export const getCellFixedWidth = (
  plugin: CellPlugin | null,
  data: DataTType
): number | undefined => {
  const width = getCellOuterDivStyle(plugin, data)?.width;
  return typeof width === 'number' ? width : undefined;
};

/**
 * Once a row contains a pixel pinned cell, the others can no longer be a fixed
 * percentage of that row: the pinned cell takes an amount the twelfths know
 * nothing about, so the percentages would leave a gap or overflow. This lets the
 * rest share what is left, still in proportion to their grid sizes.
 *
 * Must stay in agreement with getRowCellWidths, which tells the row's resize
 * handles where these cells actually end.
 *
 * Undefined for rows with no pinned cell, which therefore keep their percentage
 * widths exactly as before.
 */
export const getCellFillStyle = (
  size: number | undefined,
  rowHasFixedWidthCell: boolean,
  isFixedWidthCell: boolean
): CSSProperties | undefined =>
  rowHasFixedWidthCell && !isFixedWidthCell
    ? { flexGrow: size ?? 12, flexBasis: 0, minWidth: 0 }
    : undefined;

export const getCellInnerDivClassName = (
  cell: Cell | null,
  plugin: CellPlugin | null,
  data: unknown
) => {
  const additionalClass = plugin?.cellClassName
    ? typeof plugin?.cellClassName === 'function'
      ? plugin?.cellClassName(data)
      : plugin?.cellClassName
    : undefined;

  return (
    'react-page-cell-inner' +
    ((cell?.rows?.length ?? 0) > 0 ? '' : ' react-page-cell-inner-leaf') +
    (additionalClass ? ' ' + additionalClass : '')
  );
};

export const getCellInnerDivStylingProps = (
  cell: Cell | null,
  plugin: CellPlugin | null,
  data: DataTType
) => {
  return {
    style: getCellInnerDivStyle(cell, plugin, data),
    className: getCellInnerDivClassName(cell, plugin, data),
  };
};

export const gridClass = (size?: number): string => {
  return `react-page-cell-sm-${size || 12} react-page-cell-xs-12`;
};

export const getCellOuterDivClassName = ({
  size,
  hasInlineNeighbour,
  inline,
  hasChildren,
}: {
  size?: number;
  hasChildren: boolean;
  hasInlineNeighbour?: string;
  inline?: string | null;
}) => {
  return classNames('react-page-cell', gridClass(size), {
    'react-page-cell-has-inline-neighbour': hasInlineNeighbour,
    [`react-page-cell-inline-${inline || ''}`]: inline,
    'react-page-cell-leaf': !hasChildren,
  });
};
